import React, { useState } from 'react';
import {
  Box,
  Menu,
  Avatar,
  Typography,
  Divider,
  Button,
  IconButton,
  ListItemButton,
  List,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { IconChevronDown, IconUser, IconFileText, IconMail, IconCheck } from '@tabler/icons-react';

import { useAuth } from '@/hooks/useAuth.ts';
import { useGetMe } from '@/lib/api/me.ts';
import { useAnalytics } from '@/hooks/useAnalytics.ts';
import { Link } from 'react-router-dom';
import ContactModal from '@/components/ContactModal';

const Profile = () => {
  const [anchorEl2, setAnchorEl2] = useState<null | HTMLElement>(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const { data: userData, isLoading } = useGetMe();
  const { logout } = useAuth();
  const { trackEvent, resetUser } = useAnalytics();

  const handleClick2 = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl2(event.currentTarget);
  };

  const handleClose2 = () => {
    setAnchorEl2(null);
  };

  const handleLogout = async () => {
    try {
      trackEvent('logout');
      resetUser();
      logout();
      window.location.reload();
      handleClose2();
    } catch (error) {
      // Log error silently for debugging purposes
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Error al cerrar sesión:', error);
      }
    }
  };

  const handleOpenContactModal = () => {
    setContactModalOpen(true);
    handleClose2();
  };

  if (isLoading) {
    return <Box>Loading...</Box>;
  }

  return (
    <Box>
      <IconButton
        size="large"
        aria-label="menu"
        color="inherit"
        aria-controls="msgs-menu"
        aria-haspopup="true"
        sx={{
          ...(anchorEl2 && { borderRadius: '9px' }),
        }}
        onClick={handleClick2}
      >
        <Avatar
          src={userData?.photoUrl ?? '/images/profile/default.png'}
          alt="ProfileImg"
          sx={{
            width: 30,
            height: 30,
          }}
        />
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            maxWidth: { xs: 0, sm: 180, md: 240 },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography color="textSecondary" variant="h6" fontWeight="400" sx={{ ml: 1 }}>
            Hola
          </Typography>
          <Typography
            variant="h6"
            fontWeight="700"
            sx={{
              ml: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { xs: 0, sm: 100, md: 160 },
            }}
          >
            {userData?.name}
          </Typography>
          <IconChevronDown width="20" height="20" />
        </Box>
      </IconButton>

      <Menu
        id="msgs-menu"
        anchorEl={anchorEl2}
        keepMounted
        open={Boolean(anchorEl2)}
        onClose={handleClose2}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        sx={{
          '& .MuiMenu-paper': {
            width: '300px',
            p: 0,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <Box>
          {/* User Section */}
          <Box sx={{ p: 2, pb: 0 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Mi cuenta
            </Typography>
          </Box>

          <List sx={{ py: 0 }}>
            <ListItemButton component={Link} to="/perfil" onClick={handleClose2}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <IconUser size={20} />
              </ListItemIcon>
              <ListItemText primary="Editar mi perfil" />
            </ListItemButton>

            {userData?.role === 'investor' && (
              <ListItemButton component={Link} to="/test-idoneidad" onClick={handleClose2}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <IconCheck size={20} />
                </ListItemIcon>
                <ListItemText primary="Test idoneidad" />
              </ListItemButton>
            )}
          </List>

          <Divider sx={{ mx: 2 }} />

          {/* Support Section */}
          <Box sx={{ p: 2, pb: 0 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Soporte y ayuda
            </Typography>
          </Box>

          <List sx={{ py: 0 }}>
            <ListItemButton onClick={handleOpenContactModal}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <IconMail size={20} />
              </ListItemIcon>
              <ListItemText primary="Contactar soporte" />
            </ListItemButton>

            <ListItemButton component={Link} to="/legal" onClick={handleClose2}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <IconFileText size={20} />
              </ListItemIcon>
              <ListItemText primary="Información legal" />
            </ListItemButton>
          </List>

          <Divider sx={{ mx: 2, mt: 1 }} />

          {/* Logout Section */}
          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                py: 1.2,
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              Cerrar sesión
            </Button>
          </Box>
        </Box>
      </Menu>

      {/* Contact Modal */}
      <ContactModal open={contactModalOpen} onClose={() => setContactModalOpen(false)} />
    </Box>
  );
};

export default Profile;
